import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronDown, Search, X } from 'lucide-react'
import { apiClient } from '@/lib/api/client'
import type { PageResponse } from '@/lib/api/types'
import { cn } from '@/lib/utils'

interface EmployeeOption {
  id: string
  displayName: string
  employeeCode: string
  designationName?: string | null
  departmentName?: string | null
}

export interface EmployeePickerProps {
  value?: string | null
  onChange: (employeeId: string | null) => void
  id?: string
  placeholder?: string
  disabled?: boolean
  /** Narrows the search server-side, e.g. to one legal entity. */
  legalEntityId?: string
  'aria-label'?: string
  className?: string
}

/** How many the list shows at once. The count is stated on screen, never silently applied. */
const PAGE_SIZE = 20

/**
 * Choosing a person, out of however many there are.
 *
 * <p>This replaces a `<Select>` fed by `useEmployeeOptions(500)` — the first five hundred
 * employees by name, loaded up front. On the demo company of 1,248 that dropdown **cannot
 * reach anybody whose name sorts after the 500th**, and nothing on screen says so: the person
 * is simply not in the list, which reads as "they are not an employee". A company of ten
 * thousand would lose ninety-five per cent of its people to the same line of code.
 *
 * <p>So the search happens on the server, twenty at a time, and the component says what it is
 * showing. The three rules that follow from that:
 *
 * <ul>
 *   <li><b>It never claims to be complete.</b> When more match than are shown, it says how many
 *       and asks for a narrower search, rather than presenting a truncated list as the answer.
 *   <li><b>It shows the department and job title.</b> Large companies have several people
 *       called the same thing, and a picker of bare names makes choosing the wrong one easy —
 *       which on this product means assigning an asset or a loan to a stranger.
 *   <li><b>The current value is resolved by id.</b> Editing a record whose employee is not in
 *       the first twenty results must still show their name, not an empty box.
 * </ul>
 */
export function EmployeePicker({
  value, onChange, id, placeholder = 'Search for a person…', disabled,
  legalEntityId, className, ...aria
}: EmployeePickerProps) {
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const [debounced, setDebounced] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  // A keystroke is not a query. Without this, typing a six-letter surname is six searches, of
  // which five are thrown away and all six reach the database.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 250)
    return () => clearTimeout(timer)
  }, [term])

  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  const results = useQuery({
    queryKey: ['employees', 'picker', debounced, legalEntityId ?? null],
    enabled: open,
    queryFn: async () => (await apiClient.get<PageResponse<EmployeeOption>>('/v1/employees', {
      params: {
        size: PAGE_SIZE, sort: 'displayName,asc',
        ...(debounced ? { q: debounced } : {}),
        ...(legalEntityId ? { legalEntityId } : {}),
      },
    })).data,
    staleTime: 30_000,
  })

  // Resolved separately from the search: the chosen person is usually not in the first twenty
  // results, and an edit screen showing an empty picker looks like an unset field.
  const selected = useQuery({
    queryKey: ['employees', 'picker', 'selected', value],
    enabled: Boolean(value),
    queryFn: async () =>
      (await apiClient.get<EmployeeOption>(`/v1/employees/${value}`)).data,
    staleTime: 5 * 60_000,
  })

  const label = useMemo(() => {
    if (!value) return null
    const inResults = results.data?.content.find((row) => row.id === value)
    return inResults?.displayName ?? selected.data?.displayName ?? 'Loading…'
  }, [value, results.data, selected.data])

  const total = results.data?.totalElements ?? 0
  const shown = results.data?.content.length ?? 0

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-label={aria['aria-label']}
        disabled={disabled}
        onClick={() => setOpen((was) => !was)}
        className="flex h-10 w-full items-center justify-between gap-2 rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-card)] px-3 text-start text-sm text-[var(--color-heading)] disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span className={label ? '' : 'text-[var(--color-muted)]'}>{label ?? placeholder}</span>
        <span className="flex items-center gap-1">
          {value && (
            <X
              className="size-4 text-[var(--color-muted)]"
              aria-hidden="true"
              onClick={(event) => { event.stopPropagation(); onChange(null) }}
            />
          )}
          <ChevronDown className="size-4 text-[var(--color-muted)]" aria-hidden="true" />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] shadow-lg">
          <div className="relative border-b border-[var(--color-border)] p-2">
            <Search
              className="pointer-events-none absolute start-4 top-1/2 size-4 -translate-y-1/2 text-[var(--color-muted)]"
              aria-hidden="true"
            />
            <input
              autoFocus
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Name or employee number"
              aria-label="Search for a person"
              className="h-9 w-full rounded-[var(--radius-control)] bg-[var(--color-page)] ps-8 pe-2 text-sm outline-none"
            />
          </div>

          <ul role="listbox" className="max-h-64 overflow-y-auto p-1">
            {results.isPending && <li className="p-3 text-sm text-[var(--color-muted)]">Searching…</li>}
            {results.isError && (
              <li className="p-3 text-sm text-[var(--color-danger)]">
                The directory could not be searched.
              </li>
            )}
            {results.isSuccess && shown === 0 && (
              <li className="p-3 text-sm text-[var(--color-muted)]">
                {debounced ? `Nobody matches “${debounced}”.` : 'No employees.'}
              </li>
            )}
            {results.data?.content.map((employee) => (
              <li key={employee.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={employee.id === value}
                  onClick={() => { onChange(employee.id); setOpen(false) }}
                  className="flex w-full items-start gap-2 rounded-[var(--radius-control)] p-2 text-start text-sm hover:bg-[var(--color-page)]"
                >
                  <Check
                    className={cn('mt-0.5 size-4 shrink-0',
                      employee.id === value ? 'opacity-100' : 'opacity-0')}
                    aria-hidden="true"
                  />
                  <span>
                    {employee.displayName}
                    <span className="block text-xs text-[var(--color-muted)]">
                      {[employee.employeeCode, employee.designationName, employee.departmentName]
                        .filter(Boolean).join(' · ')}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {results.isSuccess && total > shown && (
            // Said, not silently applied. The list this replaced simply stopped at 500.
            <p className="border-t border-[var(--color-border)] p-2 text-xs text-[var(--color-muted)]">
              Showing {shown} of {total}. Type more of the name to narrow it.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
